/** The one-line "what Ella is doing" text shown while a turn runs (from the poll's activity/events). */

import type { JobActivity, JobPoll } from "../backends/types";

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * Word-tool kinds (`_ACTIVITY_KIND_BY_TOOL` in app/handlers/scholar_chat.py). The server sends facts only — a
 * paragraph range or a name as `subject`, a number as `count` — and the pane words them.
 */
const WORD_KINDS: Readonly<Record<string, (subject: string, count: number | undefined) => string>> = {
  read_word_document: (subject) => (subject ? `Reading ${subject}` : "Reading the document"),
  drafted_edits: (_s, count) => (count ? `Drafting ${plural(count, "edit")}` : "Drafting edits"),
  drafted_comments: (_s, count) => (count ? `Writing ${plural(count, "comment")}` : "Writing comments"),
  drafted_text: (subject) => (subject ? `Drafting text ${subject}` : "Drafting text"),
  ran_cleanup_checks: (_s, count) =>
    count === undefined ? "Running clean-up checks" : count === 0 ? "Clean-up checks found nothing" : `Clean-up checks found ${plural(count, "issue")}`,
  checked_citations: (_s, count) => (count ? `Checking ${plural(count, "citation")}` : "Checking citations"),
  compared_versions: (subject) => (subject ? `Comparing with “${subject}”` : "Comparing versions"),
};

// Ask kinds shared with the web app.
const KIND_WORDS: Readonly<Record<string, string>> = {
  searched_case_law: "Searching case law",
  opened_case: "Opening a case",
  checked_treatment: "Checking later treatment",
  read_source: "Reading a source",
  searched_source: "Searching a source",
  read_contract_playbook: "Reading your playbook",
  searched_web: "Searching the web",
  opened_page: "Reading a web page",
  read_file: "Reading a file",
  scanned_files: "Scanning your files",
  compared_documents: "Comparing documents",
  marked_up_contract: "Marking up the contract",
  wrote_document: "Writing a draft",
};

function clip(text: string, max: number): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length <= max ? flat : `${flat.slice(0, max - 1).trimEnd()}…`;
}

export function activityText(item: JobActivity): string {
  const subject = item.subject ? clip(item.subject, 60) : "";
  const word = WORD_KINDS[item.kind];
  if (word !== undefined) return `${word(subject, item.count)}…`;
  const words = KIND_WORDS[item.kind] ?? "Working";
  return `${words}${subject ? ` ${subject}` : ""}…`;
}

export function activityLine(poll: JobPoll): string {
  const last = poll.activity[poll.activity.length - 1];
  if (last !== undefined) return activityText(last);
  if (poll.thought) return clip(poll.thought, 120);
  const event = [...poll.events].reverse().find((e) => e.message && e.type !== "accepted");
  if (event !== undefined) return `${clip(event.message, 100).replace(/[.…]+$/, "")}…`;
  if (poll.status === "queued") return "Waiting for Ella…";
  if (poll.status === "generating") return "Writing the answer…";
  return "Reading the document…";
}
